import { useState, type ReactNode } from 'react'
import { formatPhone } from '@/lib/phone'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { BackButton } from '@/components/back-button'
import { EntityActivityBadge } from '@/components/entity-activity-badge'
import { useCustomerCommThread, useRecordCustomerComm } from '@/api/hooks'
import {
  useApproveMessage,
  useRejectMessage,
  useSendMessage,
  useUpdateMessage,
} from '@/features/communications/hooks'
import { ThreadMailboxProvider } from '@/features/communications/mailbox'
import { POConversations } from '@/features/communications/po-conversations'
import { ActionItemsList, CustomerActionCard } from '@/features/purchase-orders/actions'
import { ActionEmailContext } from '@/features/communications/action-email'
import { ActionItemSourceProvider } from '@/features/purchase-orders/action-source'
import {
  customerDisplayName,
  toPurchaseOrderAction,
  toConversationThread,
  type CustomerThread,
} from '@/features/customer-comms/thread-model'
import { formatApiErrorMessage } from '@/lib/api-error'
import { usePageTitle } from '@/lib/page-title'
import { useActionFocus, useMessageFocus } from '@/hooks/use-action-focus'
import { cn, formatStatus } from '@/lib/utils'
import { WaitingOn } from '@/features/owed-items/waiting-on'
import { CustomerContextPanel } from '@/features/customer-comms/customer-context-panel'

const CUSTOMER_SIGNOFF = 'Thank you,\nUSA Restaurant Suppliers\nCustomer Service\nstatus@shopusars.com'

const linkClass = 'font-medium text-primary hover:underline'

function Fact({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex min-w-0 items-baseline gap-1.5', className)}>
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  )
}

/**
 * Who the customer is and every record this conversation belongs to, each one a link. The side panel
 * carries the customer, project and order on desktop (USA-1695), so they show here only below it.
 */
function ThreadFacts({ thread }: { thread: CustomerThread }) {
  const facts: ReactNode[] = []
  const inPanel = 'lg:hidden'
  if (thread.customer_group) {
    facts.push(
      <Fact key="customer" label="Customer" className={inPanel}>
        <Link to="/customers/$groupId" params={{ groupId: thread.customer_group.uuid }} className={linkClass}>
          {thread.customer_group.name}
        </Link>
      </Fact>,
    )
  }
  if (thread.project) {
    facts.push(
      <Fact key="project" label="Project" className={inPanel}>
        <Link to="/projects/$projectId" params={{ projectId: thread.project.uuid }} className={linkClass}>
          {thread.project.name}
        </Link>
      </Fact>,
    )
  }
  if (thread.order) {
    facts.push(
      <Fact key="order" label="Order" className={thread.project ? inPanel : undefined}>
        <Link to="/orders/$orderId" params={{ orderId: thread.order.uuid }} className={linkClass}>
          {thread.order.order_number || thread.order.name || `#${thread.order.id}`}
        </Link>
      </Fact>,
    )
  }
  if (thread.purchase_order) {
    facts.push(
      <Fact key="po" label="PO">
        <Link to="/purchase-orders/$poId" params={{ poId: thread.purchase_order.uuid }} className={linkClass}>
          {thread.purchase_order.po_number}
        </Link>
      </Fact>,
    )
  }
  if (thread.shipment) {
    facts.push(
      <Fact key="shipment" label="Shipment">
        <Link to="/shipments/$shipmentId" params={{ shipmentId: thread.shipment.uuid }} className={linkClass}>
          {thread.shipment.carrier_name || formatStatus(thread.shipment.status)}
        </Link>
      </Fact>,
    )
  }
  if (thread.recipient_phone) {
    facts.push(
      <Fact key="phone" label="Phone">
        {formatPhone(thread.recipient_phone)}
      </Fact>,
    )
  }
  if (facts.length === 0) return null
  return <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">{facts}</dl>
}

function CustomerThreadView({
  threadId,
  actionUuid,
  messageId,
}: {
  threadId: number
  actionUuid?: string
  messageId?: number
}) {
  const { data: thread, isLoading } = useCustomerCommThread(threadId)
  usePageTitle(thread?.subject)
  useActionFocus(actionUuid, !!thread)
  useMessageFocus(messageId, !!thread)
  // Inside the customer mailbox these act on the customer thread routes and cache.
  const approve = useApproveMessage()
  const reject = useRejectMessage()
  const send = useSendMessage()
  const update = useUpdateMessage()
  const record = useRecordCustomerComm(threadId)
  const failed = (title: string) => (error: unknown) => toast.error(formatApiErrorMessage(title, error))
  const [showResolvedActions, setShowResolvedActions] = useState(false)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!thread) {
    return (
      <div className="mx-auto max-w-7xl space-y-2">
        <BackButton fallback={{ to: '/customer-comms' }} fallbackLabel="Back to Communications" />
        <p className="text-muted-foreground">Communication thread not found</p>
      </div>
    )
  }

  const actions = thread.actions.map(toPurchaseOrderAction)
  // Its action items comment in its project's comments (USA-1443); a thread with no project has none.
  const actionComments = thread.action_project_uuid ? { projectUuid: thread.action_project_uuid } : undefined
  const customerName = customerDisplayName(thread)
  const greetingName = thread.recipient_name?.trim() || 'Customer'

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="space-y-2">
        {thread.customer_group ? (
          <BackButton
            fallback={{ to: '/customers/$groupId', params: { groupId: thread.customer_group.uuid }, search: { tab: 'comms' } }}
            fallbackLabel="Back to Customer"
          />
        ) : (
          <BackButton fallback={{ to: '/customer-comms' }} fallbackLabel="Back to Communications" />
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="min-w-0 break-words text-2xl font-bold tracking-tight">{customerName}</h1>
          {thread.notification_type && (
            <Badge variant="outline">{formatStatus(thread.notification_type)}</Badge>
          )}
          <EntityActivityBadge entityType="customer_communication" entityId={thread.id} />
        </div>
        <ThreadFacts thread={thread} />
      </div>

      {/* Two columns on desktop: the work on the left, what it belongs to on the right. Below lg the
          panel follows the conversation. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-6">
      <WaitingOn scope={{ kind: 'customer_thread', threadId: thread.id, threadUuid: thread.uuid }} />

      <ActionItemSourceProvider value={{ kind: 'customer_thread', threadId: thread.id }}>
        <section className="space-y-4" aria-label="Actions">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</h3>
          <ActionItemsList
            poId=""
            openActions={actions.filter((action) => action.status === 'open')}
            resolvedActions={actions.filter((action) => action.status !== 'open')}
            reopens={thread.action_reopens}
            shares={[]}
            lineItems={[]}
            customerName={thread.recipient_name}
            customerEmail={thread.recipient_email}
            comments={actionComments}
            showResolvedActions={showResolvedActions}
            setShowResolvedActions={setShowResolvedActions}
          />
        </section>

      <ActionEmailContext.Provider
        value={{
          focusMessageId: messageId,
          renderAction: (item) => {
            const action = actions.find((candidate) => candidate.id === item.id)
            return action ? (
              <CustomerActionCard
                action={action}
                reopens={thread.action_reopens}
                customerName={thread.recipient_name}
                customerEmail={thread.recipient_email}
                comments={actionComments}
              />
            ) : null
          },
        }}
      >
      <POConversations
        title="Conversation"
        threads={[toConversationThread(thread)]}
        actions={actions}
        isLoading={false}
        hasPoPdf={false}
        hasQuotePdf={false}
        shipmentDocOptions={[]}
        contextForThread={() => ({
          greeting: `Dear ${greetingName},`,
          signoff: CUSTOMER_SIGNOFF,
          poDocuments: false,
          onRecord: (bodyText, source) =>
            record.mutate(
              { body_text: bodyText, message_type: 'note', source },
              {
                onSuccess: () => toast.success('Communication recorded'),
                onError: failed('Could not record it'),
              },
            ),
          isRecording: record.isPending,
          recordPlaceholder: 'Summarize what the customer communicated...',
        })}
        onApprove={(uuid) => approve.mutate(uuid, { onError: failed('Not sent') })}
        onReject={(uuid) => reject.mutate(uuid, { onError: failed('Not rejected') })}
        onSend={(uuid) => send.mutate(uuid, { onError: failed('Not sent') })}
        onUpdate={(uuid, body) =>
          update.mutate({ messageUuid: uuid, body }, { onError: failed('Draft not saved') })
        }
        newThreadAction={null}
        emptyState={null}
      />
      </ActionEmailContext.Provider>
      </ActionItemSourceProvider>
      </div>
      <aside className="min-w-0 space-y-4" aria-label="Customer and project">
        <CustomerContextPanel thread={thread} />
      </aside>
      </div>
    </div>
  )
}

/** A customer email thread on the PO thread components (USA-1328), with its action items. */
export function CustomerCommunicationDetailPage({
  threadId,
  actionUuid,
  messageId,
}: {
  threadId: number
  /** The action item opened from the Action Items page: scrolled to and highlighted. */
  actionUuid?: string
  /** The email an action card linked to: scrolled to (USA-1413). */
  messageId?: number
}) {
  return (
    <ThreadMailboxProvider value="customer">
      <CustomerThreadView threadId={threadId} actionUuid={actionUuid} messageId={messageId} />
    </ThreadMailboxProvider>
  )
}
